/** @jsxImportSource @emotion/react */
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGrid, faRows } from "@fortawesome/pro-solid-svg-icons";
import Colors, { shadeColor } from "styles/colors";
import { ToggleButton } from "components/atoms/ToggleButton.atom";
import { ToggleButtonGroup } from "components/molecules/ToggleButtonGroup.molecule";
import { SearchInput } from "pages/reports/bi-dashboard-next/components/molecules/Reports.SearchInput.molecule";
import { ReportsTabs } from "pages/reports/bi-dashboard-next/components/molecules/Reports.Tabs.molecule";
import { ReportTypeaheadDTO } from "pages/reports/bi-dashboard-next/models/SearchTypeaheadDTO";
import { DistinctTagDTO } from "pages/reports/bi-dashboard-next/models/DistinctTagDTO";
import { ReportTabType } from "pages/reports/bi-dashboard-next/models/ReportTabType";
import { useTranslation } from "react-i18next";

interface ReportsTopBarProps {
  activeTab: ReportTabType;
  onTabChange: (tab: ReportTabType) => void;
  searchValue: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectTag: (tag: DistinctTagDTO) => void;
  onSelectReport: (report: ReportTypeaheadDTO) => void;
  viewMode: string;
  onViewModeChange: (value: string) => void;
}

export const ReportsTopBar: React.FC<ReportsTopBarProps> = ({
  activeTab,
  onTabChange,
  searchValue,
  onSearchChange,
  onSelectTag,
  onSelectReport,
  viewMode,
  onViewModeChange,
}) => {
  const { t } = useTranslation("reports");
  return (
    <div
      css={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        alignContent: "center",
        justifyContent: "space-between",
        background: "transparent",
        position: "relative",
        paddingTop: "10px",
      }}
    >
      <ReportsTabs activeTab={activeTab} onTabChange={onTabChange} />
      <div
        css={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
          columnGap: 10,
          margin: "10px 10px 10px 10px",
          flex: "1 1 auto",
        }}
      >
        <SearchInput
          value={searchValue}
          onChange={onSearchChange}
          onSelectTag={onSelectTag}
          onSelectReport={onSelectReport}
        />
        <ToggleButtonGroup
          exclusive
          value={viewMode}
          onChange={(value) => onViewModeChange(value ?? "row")}
        >
          <ToggleButton
            value="row"
            aria-label={t("Row View")}
            css={{
              color:
                viewMode === "row"
                  ? Colors.text.WHITE
                  : Colors.biDashboardView.FOCUS_COLOR,
              backgroundColor:
                viewMode === "row"
                  ? Colors.biDashboardView.FOCUS_COLOR
                  : Colors.biDashboardView.CARD_BACKGROUND,
              border: `1px solid ${shadeColor(
                Colors.biDashboardView.FOCUS_COLOR,
                -0.15,
              )}`,
            }}
          >
            <FontAwesomeIcon icon={faRows} css={{ fontSize: 18 }} />
          </ToggleButton>
          <ToggleButton
            value="grid"
            aria-label={t("Grid View")}
            css={{
              color:
                viewMode === "grid"
                  ? Colors.text.WHITE
                  : Colors.biDashboardView.FOCUS_COLOR,
              backgroundColor:
                viewMode === "grid"
                  ? Colors.biDashboardView.FOCUS_COLOR
                  : Colors.biDashboardView.CARD_BACKGROUND,
              border: `1px solid ${shadeColor(
                Colors.biDashboardView.FOCUS_COLOR,
                -0.15,
              )}`,
            }}
          >
            <FontAwesomeIcon icon={faGrid} css={{ fontSize: 18 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>
    </div>
  );
};
